import { getApiInst } from '~/atoms/MangoApi'

import type { TransportResponse } from '@mangovoice/api-client'
import type { SessionUser } from '@mangovoice/types'
import {
  createUserApi,
  mapUserDetailToMyProfile,
  MY_PROFILE_URL_REGEX,
  type UserApiClient,
} from '@mangovoice/users'
import type { UserDetailResponse } from '@mangovoice/users'

/**
 * Creates a UserApi for @mangovoice/users for the web-app.
 * getMyProfile: GET /app/v2/users/{id}/ → MyProfile.
 * updateMyProfile: PATCH /app/v2/users/{id}/ with first_name, last_name.
 */
export function createWebAppUserApi(
  sessionUser: SessionUser | undefined,
): ReturnType<typeof createUserApi> {
  const mangoApi = getApiInst()

  const client: UserApiClient = {
    get: async <T>(
      url: string,
      params?: Record<string, unknown>,
    ): Promise<TransportResponse<T>> => {
      if (!sessionUser)
        return { ok: false, data: null, status: 0, problem: 'UNKNOWN_ERROR' }
      if (!MY_PROFILE_URL_REGEX.test(url))
        return { ok: false, data: null, status: 0, problem: 'UNKNOWN_ERROR' }
      const res = await mangoApi.requestGet<UserDetailResponse>(url, params)
      if (!res.ok || !res.data) {
        return {
          ok: false,
          data: null,
          status: res.status ?? 0,
          problem: 'UNKNOWN_ERROR',
        }
      }
      const profile = mapUserDetailToMyProfile(res.data) as T
      return { ok: true, data: profile, status: res.status ?? 200 }
    },
    post: async <T>(
      _url: string,
      _data?: Record<string, unknown>,
    ): Promise<TransportResponse<T>> => {
      return { ok: false, data: null, status: 501, problem: 'NOT_IMPLEMENTED' }
    },
    patch: async <T>(
      url: string,
      data?: Record<string, unknown>,
    ): Promise<TransportResponse<T>> => {
      if (!sessionUser)
        return { ok: false, data: null, status: 0, problem: 'UNKNOWN_ERROR' }
      if (!MY_PROFILE_URL_REGEX.test(url))
        return { ok: false, data: null, status: 0, problem: 'UNKNOWN_ERROR' }
      const res = await mangoApi.requestPatch<unknown>(url, data)
      if (!res.ok) {
        return {
          ok: false,
          data: null,
          status: res.status ?? 0,
          problem: 'UNKNOWN_ERROR',
        }
      }
      return { ok: true, data: res.data as T, status: res.status ?? 200 }
    },
    delete: async <T>(_url: string): Promise<TransportResponse<T>> => {
      return { ok: false, data: null, status: 0, problem: 'UNKNOWN_ERROR' }
    },
  }
  return createUserApi(client)
}
